import React, { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { isClientFacingPath } from '../lib/publicRoutes';
import ClientLoadFailure from './ClientLoadFailure';
import {
  isStaleBuildError,
  hardRefresh,
  clearRecoveryMarker,
  cleanRecoveryParams,
} from '../lib/chunkRecovery';

// Re-exported for any existing caller that imported these from here.
export { isStaleBuildError, hardRefresh };

/**
 * BuildUpdateBar — the banner UI only. It owns NO chunk-failure decision: that
 * lives entirely in lib/chunkRecovery. This component just listens for the two
 * events the app raises and shows the matching banner:
 *   - `staffed:newbuild`  → a newer build is available (proactive, user-driven).
 *   - `staffed:chunkstuck` → recovery is exhausted; offer a manual Reload.
 * On a clean mount it clears the one-retry marker and strips the cache-bust
 * param from the URL.
 */
export default function BuildUpdateBar() {
  const [mode, setMode] = useState(null);
  useEffect(() => {
    let stuck = false;
    const onNew = () => setMode((m) => (m === 'stuck' ? m : 'update'));
    const onStuck = () => { stuck = true; setMode('stuck'); };
    if (window.__staffedNewBuild) onNew();
    window.addEventListener('staffed:newbuild', onNew);
    window.addEventListener('staffed:chunkstuck', onStuck);

    // App mounted successfully → clear the one-retry marker so a later,
    // unrelated failure gets its own single retry, and take the cache-bust
    // param out of the address bar (client links must be clean). Skip if a
    // chunk already got stuck on this boot.
    const settle = setTimeout(() => {
      if (stuck || window.__staffedChunkFailedThisBoot) return;
      clearRecoveryMarker();
      cleanRecoveryParams();
    }, 5000);

    return () => {
      clearTimeout(settle);
      window.removeEventListener('staffed:newbuild', onNew);
      window.removeEventListener('staffed:chunkstuck', onStuck);
    };
  }, []);

  if (!mode) return null;
  const stuck = mode === 'stuck';
  // A client on their own link must never be left on a half-rendered page, and
  // must never be shown STAFFED's version-management copy.
  if (stuck && isClientFacingPath(window.location.pathname)) return <ClientLoadFailure />;
  return (
    <div
      data-testid="build-update-bar"
      className={stuck
        ? 'fixed top-3 left-1/2 -translate-x-1/2 z-[9999] max-w-[92vw]'
        : 'fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] max-w-[92vw]'}
    >
      <div className="flex items-center gap-3 rounded-full px-4 py-2.5 shadow-lg border text-sm bg-slate-900 border-slate-700 text-white">
        <RefreshCw className="h-4 w-4 shrink-0" />
        <span className="truncate">A new version of STAFFED is available.</span>
        <button
          type="button"
          onClick={hardRefresh}
          data-testid="build-update-refresh"
          className="shrink-0 rounded-full px-3 py-1 font-medium bg-white text-slate-900 hover:bg-slate-100"
        >
          Reload
        </button>
        {!stuck && (
          <button
            type="button"
            onClick={() => setMode(null)}
            data-testid="build-update-dismiss"
            className="shrink-0 text-slate-400 hover:text-white"
          >
            Later
          </button>
        )}
      </div>
    </div>
  );
}
